import React, { useEffect, useState } from 'react';
import { useDeskInPlan } from '../../hooks/use-desk-in-plan';
import { w } from '../../../stays/wording';
import styled from '@emotion/styled';
import { cancelReservation, listBuildingReservations, listListingReservations } from '../../../api/stays-client';
import type { StayReservation, StayReservationList, StaysPolicy } from '../../../interfaces/stays';
import { useResource } from '../../hooks/use-resource';
import { ConfirmDialog } from '../../modal/confirm-dialog';
import { ClockNote } from '../../clock-note/clock-note';
import { ListSkeleton, LoadError, skeletonHeight } from '../dashboard/dashboard-shared-components/load-state';
import { Presence, Staleable, StateSwitch } from '../../../motion';
import { Table, TableCaption, TableFrame, Tbody, Th, Thead, Tr } from '../../table/list-table';
import toast from '../../../utils/toast';
import { refusalText } from '../dashboard/dashboard-stays-components/stays-labels';
import { Card, Empty, EmptyText } from '../dashboard/dashboard-stays-components/dashboard-stays.styled';
import { palette, spacing, typography } from '../../../style-utils/theme';
import { alertHours, buildingClock, datesLine, firstName } from './stays-reservations-model';
import { StayRow } from './stay-row';
import { StayLinkPanel } from './stay-link-panel';

/**
 * UPCOMING STAYS (ai-plans/stays plan S3) — the host's list of stays for one
 * building (the Stays page) or one listing (the listing page).
 *
 * Dates are the building's calendar days and arrival times its wall clock, so
 * the card carries ONE ClockNote, on the building's clock. "Signed in HH:MM" is
 * an instant on the reader's own clock and carries data-at (stay-row).
 *
 * Loading, failed and empty are three renders; a refetch (after a save, a
 * cancel, a new link) keeps the rows and dims them (Staleable).
 */

export interface StaysReservationsCardProps {
  /** The Stays page: the selected building. Null shows the "choose a building" line. */
  buildingId?: number | null;
  /** The listing page: that listing's stays (wins over buildingId). */
  listingId?: string | null;
  policy?: StaysPolicy | null;
  /** stays:manage — the rows' Link and Cancel stay. */
  canManage: boolean;
  /** Bump to read the list again (a stay was added elsewhere on the page). */
  reloadKey?: number;
  /** The building's today from the list response, for the add form's minimum date. */
  onToday?: (today: string | null) => void;
  /** Beside the title — the listing page's own "Add a stay". */
  action?: React.ReactNode;
  emptyText?: string;
}

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.xs} ${spacing.sm};
  min-width: 0;
`;

const HeadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
`;

const Hint = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

export const STAYS_EMPTY_TEXT = 'No stays booked yet. Add one from a listing above.';

export const StaysReservationsCard: React.FC<StaysReservationsCardProps> = ({
  buildingId = null,
  listingId = null,
  policy,
  canManage,
  reloadKey = 0,
  onToday,
  action,
  emptyText = STAYS_EMPTY_TEXT,
}) => {
  const enabled = !!listingId || buildingId !== null;
  const desk = useDeskInPlan();
  const resource = useResource<StayReservationList>(
    () => (listingId ? listListingReservations(listingId) : listBuildingReservations(buildingId as number)),
    { enabled, deps: [listingId, buildingId, reloadKey], fallbackMessage: 'Could not load the stays.' }
  );
  const list = resource.data || null;
  const items: StayReservation[] = (list && Array.isArray(list.items) && list.items) || [];
  const today = list && typeof list.today === 'string' ? list.today : null;

  const [linkFor, setLinkFor] = useState<StayReservation | null>(null);
  const [cancelling, setCancelling] = useState<StayReservation | null>(null);

  useEffect(() => {
    if (onToday) onToday(today);
  }, [onToday, today]);

  const hours = alertHours(policy);
  const zone = (list && (list.tz || list.building?.timezone)) || items[0]?.building_timezone || null;
  const titleId = listingId ? `stays-upcoming-${listingId}` : 'stays-upcoming';

  const cancel = async (stay: StayReservation) => {
    const first = firstName(stay);
    try {
      await cancelReservation(stay.id);
      toast.success(`${first}'s stay is cancelled.`);
    } catch (error) {
      toast.error(refusalText(error as { code?: string | null; message?: string }, 'Could not cancel the stay. Try again.'));
    }
    void resource.reload();
  };

  type View = 'loading' | 'error' | 'empty' | 'ready';
  const view: View = resource.isLoading
    ? 'loading'
    : resource.error && !list
    ? 'error'
    : items.length
    ? 'ready'
    : 'empty';

  return (
    <Card aria-labelledby={titleId} data-testid="stays-upcoming">
      <Head>
        <HeadText>
          <Title id={titleId}>Upcoming stays</Title>
          {enabled && list ? <ClockNote clock={buildingClock(zone)} /> : null}
        </HeadText>
        {action}
      </Head>

      {!enabled ? (
        <Hint>{w('chooseBuilding', desk)}</Hint>
      ) : (
        <>
          {resource.error && list ? (
            <LoadError message="Could not refresh the stays." onRetry={() => void resource.reload()} />
          ) : null}
          <StateSwitch state={view} minHeight={skeletonHeight(3, 64)}>
            {view === 'loading' ? (
              <ListSkeleton rows={3} rowHeight={64} label="stays" />
            ) : view === 'error' ? (
              <LoadError message="Could not load the stays." onRetry={() => void resource.reload()} />
            ) : view === 'empty' ? (
              <Empty>
                <EmptyText>{emptyText}</EmptyText>
              </Empty>
            ) : (
              <Staleable stale={resource.isRefreshing}>
                <TableFrame>
                  <Table data-stack="lg">
                    <TableCaption>Upcoming stays</TableCaption>
                    <Thead>
                      <Tr>
                        <Th scope="col">Guest</Th>
                        <Th scope="col">Listing</Th>
                        <Th scope="col">Dates</Th>
                        <Th scope="col">Party</Th>
                        <Th scope="col">Arrival</Th>
                        <Th scope="col">Check-in</Th>
                        {canManage ? <Th scope="col">Actions</Th> : null}
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Presence key={listingId || String(buildingId)}>
                        {items.map((stay, index) => (
                          <StayRow
                            key={stay.id}
                            stay={stay}
                            index={index}
                            policy={policy}
                            alertHours={hours}
                            canManage={canManage}
                            onLink={setLinkFor}
                            onCancel={setCancelling}
                          />
                        ))}
                      </Presence>
                    </Tbody>
                  </Table>
                </TableFrame>
              </Staleable>
            )}
          </StateSwitch>
        </>
      )}

      <StayLinkPanel
        open={!!linkFor}
        reservation={linkFor}
        onClose={() => setLinkFor(null)}
        onChanged={() => void resource.reload()}
      />
      <ConfirmDialog
        open={!!cancelling}
        title={cancelling ? `Cancel ${firstName(cancelling)}'s stay?` : 'Cancel this stay?'}
        message={cancelling ? `${datesLine(cancelling)}. The check-in link stops working.` : ''}
        primaryLabel={cancelling ? `Cancel ${firstName(cancelling)}'s stay` : 'Cancel stay'}
        primaryVariant="destructive"
        dismissLabel="Keep stay"
        onPrimary={async () => {
          if (cancelling) await cancel(cancelling);
          setCancelling(null);
        }}
        onDismiss={() => setCancelling(null)}
      />
    </Card>
  );
};
